import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, sendMessage, mediaSrc, uploadMedia, dataUrlToBase64 } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MarkupEditor } from "./MarkupEditor";
import { CompareView } from "./CompareView";
import { TextOverlays } from "./TextOverlays";
import { VoiceDraftButton } from "./VoiceDraftButton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { shareNative, shareWhatsApp, copyLink, saveFiles, saveToDevice, canShareFiles, mediaFilename } from "../lib/share";
import { loadCanvasImage } from "../lib/imageLoader";
import { toast } from "sonner";
import { ForwardDialog } from "./ForwardDialog";
import { X, PenLine, MessageSquareQuote, ClipboardPlus, GitCompareArrows, ChevronLeft, ChevronRight, Share2, ZoomIn, ZoomOut, RotateCcw, MessageCircle, Link2, Download, Sparkles, CheckSquare, Forward } from "lucide-react";

async function cropFromMedia(media, region) {
  // Canvas-safe load (blob route) — works in the native app where cross-origin
  // images taint the canvas and toDataURL() throws.
  const img = await loadCanvasImage(mediaSrc(media));
  const sx = region.x * img.naturalWidth;
  const sy = region.y * img.naturalHeight;
  const sw = Math.max(10, region.w * img.naturalWidth);
  const sh = Math.max(10, region.h * img.naturalHeight);
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export function PhotoViewer({ open, media, message, conversation, onClose, onReplyToArea, onPrev, onNext, gallery, onSelectItem }) {
  const { user, t, lang } = useApp();
  const [versions, setVersions] = useState([]);
  const [active, setActive] = useState(media);
  const [mode, setMode] = useState("view"); // view | reply-area | task-area
  const [region, setRegion] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [markupOpen, setMarkupOpen] = useState(false);
  const [taskDialog, setTaskDialog] = useState(null); // {region, cropDataUrl}
  const [compare, setCompare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const imgWrapRef = useRef(null);

  // ---- Pinch / wheel / double-tap zoom + pan (view mode only) ----
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  zoomRef.current = zoom;
  panRef.current = pan;
  const gestureRef = useRef(null); // {type:'pinch'|'pan', ...}
  const lastTapRef = useRef(0);
  const swipeRef = useRef(null); // {x, y, t} — horizontal swipe → prev/next photo (mobile)
  const [dragY, setDragY] = useState(0); // vertical drag-down → dismiss viewer (mobile)
  const MIN_Z = 1;
  const MAX_Z = 5;
  const clampZ = (z) => Math.min(MAX_Z, Math.max(MIN_Z, z));
  const resetZoom = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); gestureRef.current = null; }, []);

  const loadVersions = useCallback(async () => {
    if (!media) return;
    try {
      const res = await api.get(`/media/${media.id}/versions`);
      setVersions(res.data);
    } catch (e) {
      setVersions([media]);
    }  }, [media]);

  useEffect(() => {
    if (open) {
      setActive(media);
      setMode("view");
      setRegion(null);
      loadVersions();
      resetZoom();
      setDragY(0);
    }
  }, [open, media, loadVersions, resetZoom]);

  // Reset zoom when switching versions or entering a selection mode
  useEffect(() => { resetZoom(); }, [active?.id, mode, resetZoom]);

  // Keyboard gallery navigation (left/right arrows) while just viewing
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (mode !== "view") return;
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mode, onPrev, onNext]);

  if (!open || !media) return null;

  const jobId = active?.job_id || conversation?.job_id || null;
  const participants = conversation?.participants || [];
  const shareUrl = mediaSrc(active);
  const shareName = mediaFilename(active, message?.meta?.linked_job || conversation?.job);

  // ---- Zoom gesture handlers (active only while just viewing) ----
  const touchDist = (e) => {
    const [a, b] = [e.touches[0], e.touches[1]];
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  };
  const zoomTouchStart = (e) => {
    if (mode !== "view") return;
    if (e.touches.length === 2) {
      swipeRef.current = null;
      setDragY(0);
      gestureRef.current = { type: "pinch", startDist: touchDist(e), startZoom: zoomRef.current };
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        // Double tap: toggle 1x <-> 2.5x
        swipeRef.current = null;
        if (zoomRef.current > 1) resetZoom();
        else setZoom(2.5);
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;
      if (zoomRef.current > 1) {
        gestureRef.current = {
          type: "pan",
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startPan: { ...panRef.current },
        };
      } else {
        // At 1x zoom a horizontal swipe navigates between photos
        swipeRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: now };
      }
    }
  };
  const zoomTouchMove = (e) => {
    const g = gestureRef.current;
    if (mode !== "view") return;
    if (g && g.type === "pinch" && e.touches.length === 2) {
      setZoom(clampZ(g.startZoom * (touchDist(e) / g.startDist)));
    } else if (g && g.type === "pan" && e.touches.length === 1) {
      setPan({
        x: g.startPan.x + (e.touches[0].clientX - g.startX),
        y: g.startPan.y + (e.touches[0].clientY - g.startY),
      });
    } else if (!g && swipeRef.current && e.touches.length === 1 && zoomRef.current <= 1.02) {
      // Drag-down to dismiss: the photo follows the finger
      const dy = e.touches[0].clientY - swipeRef.current.y;
      const dx = Math.abs(e.touches[0].clientX - swipeRef.current.x);
      setDragY(dy > 0 && dy > dx ? dy : 0);
    }
  };
  const zoomTouchEnd = (e) => {
    if (e.touches.length === 0) {
      gestureRef.current = null;
      const s = swipeRef.current;
      swipeRef.current = null;
      if (s && mode === "view" && zoomRef.current <= 1.02 && e.changedTouches && e.changedTouches[0]) {
        const dx = e.changedTouches[0].clientX - s.x;
        const dy = e.changedTouches[0].clientY - s.y;
        const dt = Date.now() - s.t;
        // Swipe DOWN → close the viewer (native photo-viewer feel)
        if (dy > 90 && dy > Math.abs(dx) * 1.2) {
          setDragY(dy + 260);
          setTimeout(() => onClose && onClose(), 130);
          return;
        }
        // Swipe left → next photo | swipe right → previous photo
        if (dt < 700 && Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          setDragY(0);
          if (dx < 0 && onNext) onNext();
          else if (dx > 0 && onPrev) onPrev();
          return;
        }
      }
      setDragY(0); // spring back
    }
    if (zoomRef.current <= 1.02 && zoomRef.current !== 1) resetZoom();
  };
  const zoomWheel = (e) => {
    if (mode !== "view") return;
    const next = clampZ(zoomRef.current - Math.sign(e.deltaY) * 0.3);
    setZoom(next);
    if (next <= 1) setPan({ x: 0, y: 0 });
  };
  const zoomMouseDown = (e) => {
    if (mode !== "view" || zoomRef.current <= 1) return;
    gestureRef.current = { type: "pan", startX: e.clientX, startY: e.clientY, startPan: { ...panRef.current } };
  };
  const zoomMouseMove = (e) => {
    const g = gestureRef.current;
    if (!g || g.type !== "pan" || mode !== "view") return;
    e.preventDefault();
    setPan({ x: g.startPan.x + (e.clientX - g.startX), y: g.startPan.y + (e.clientY - g.startY) });
  };
  const zoomMouseUp = () => { gestureRef.current = null; };
  const zoomDblClick = () => {
    if (mode !== "view") return;
    if (zoomRef.current > 1) resetZoom();
    else setZoom(2.5);
  };

  // Share the actual image file (thumbnail + "OnSiteComms_date_time.jpg" in the sheet);
  // link only as a last resort when files can't be shared.
  const doNativeShare = async () => {
    const r = await saveFiles([{ url: shareUrl, filename: shareName }]);
    if (r === "shared" || r === "cancelled") return;
    if (r === "downloaded") { toast.success(t("saved_to_device")); return; }
    const ok = await shareNative({ url: shareUrl, filename: shareName, title: shareName });
    if (!ok) toast.info(t("share_unavailable"));
  };
  const doWhatsApp = async () => {
    // native/mobile: the photo itself goes through the share sheet (pick WhatsApp there)
    if (await canShareFiles()) {
      const r = await saveFiles([{ url: shareUrl, filename: shareName }], shareName);
      if (r === "shared" || r === "cancelled") return;
    }
    shareWhatsApp({ url: shareUrl, text: shareName });
  };

  // ---- region selection handlers ----
  const relPos = (e) => {
    const rect = imgWrapRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.min(1, Math.max(0, (cx - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (cy - rect.top) / rect.height)),
    };
  };

  const areaDown = (e) => {
    if (mode === "view") return;
    e.preventDefault();
    const p = relPos(e);
    setDragStart(p);
    setRegion({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const areaMove = (e) => {
    if (!dragStart) return;
    e.preventDefault();
    const p = relPos(e);
    setRegion({
      x: Math.min(dragStart.x, p.x),
      y: Math.min(dragStart.y, p.y),
      w: Math.abs(p.x - dragStart.x),
      h: Math.abs(p.y - dragStart.y),
    });
  };
  const areaUp = async () => {
    if (!dragStart || !region) return;
    setDragStart(null);
    if (region.w < 0.03 || region.h < 0.03) {
      setRegion(null);
      return;
    }
    if (mode === "reply-area") {
      setBusy(true);
      try {
        const cropDataUrl = await cropFromMedia(active, region);
        const cropMedia = await uploadMedia({ base64: dataUrlToBase64(cropDataUrl), jobId, uploadedBy: user.id });
        onReplyToArea && onReplyToArea({ media_id: active.id, crop_media_id: cropMedia.id, cropDataUrl });
        setMode("view");
        setRegion(null);
        onClose();
      } catch (e) {
        console.error("crop (reply-area) failed:", e);
        toast.error(t("crop_failed"));
        setRegion(null);
        setMode("view");
      } finally {
        setBusy(false);
      }
    } else if (mode === "task-area") {
      setBusy(true);
      try {
        const cropDataUrl = await cropFromMedia(active, region);
        setTaskDialog({ region: { ...region }, cropDataUrl });
      } catch (e) {
        console.error("crop (task-area) failed:", e);
        toast.error(t("crop_failed"));
      } finally {
        setBusy(false);
        setMode("view");
        setRegion(null);
      }
    }
  };

  // ---- markup save ----
  const handleMarkupSave = async (dataUrl, textOverlays = []) => {
    setMarkupOpen(false);
    setBusy(true);
    try {
      const newMedia = await uploadMedia({
        base64: dataUrlToBase64(dataUrl),
        jobId,
        category: active.category,
        parentId: active.id,
        versionLabel: `Markup — ${user.name.split(" ")[0]}`,
        uploadedBy: user.id,
        textOverlays,
      });
      if (conversation && conversation.id) {
        await sendMessage({
          conversation_id: conversation.id,
          sender_id: user.id,
          type: "photo",
          content: "",
          media_ids: [newMedia.id],
          meta: { category: active.category, markup_of: active.original_id || active.id },
        });
        toast.success(t("markup") + " ✓");
      }
      await loadVersions();
      setActive({ ...newMedia });
    } catch (e) {
      toast.error("Markup save failed");
    } finally {
      setBusy(false);
    }
  };

  const openCompare = () => {
    if (versions.length >= 2) {
      const original = versions[0];
      const other = active.id === original.id ? versions[versions.length - 1] : original;
      setCompare({ left: other, right: active });
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[90] bg-black/95 flex flex-col pt-safe"
        style={dragY > 0 ? { backgroundColor: `rgba(0,0,0,${Math.max(0.35, 0.95 - dragY / 500)})` } : undefined}
        data-testid="photo-viewer"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 py-2">
          <Button variant="ghost" size="icon" data-testid="photo-viewer-close" onClick={onClose} className="text-white hover:bg-white/10 h-11 w-11">
            <X className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-1 flex-wrap justify-end">
            <Button variant="ghost" size="sm" data-testid="photo-viewer-markup" onClick={() => setMarkupOpen(true)} className="text-white hover:bg-white/10 h-10">
              <PenLine className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("markup")}</span>
            </Button>
            {onReplyToArea && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="photo-viewer-reply-area"
                onClick={() => { setMode(mode === "reply-area" ? "view" : "reply-area"); setRegion(null); }}
                className={`h-10 hover:bg-white/10 ${mode === "reply-area" ? "text-accent" : "text-white"}`}
              >
                <MessageSquareQuote className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("reply_to_area")}</span>
              </Button>
            )}
            {conversation && participants.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="task-create-from-photo"
                onClick={() => { setMode(mode === "task-area" ? "view" : "task-area"); setRegion(null); }}
                className={`h-10 hover:bg-white/10 ${mode === "task-area" ? "text-accent" : "text-white"}`}
              >
                <ClipboardPlus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("create_task")}</span>
              </Button>
            )}
            {versions.length >= 2 && (
              <Button variant="ghost" size="sm" data-testid="photo-viewer-compare" onClick={openCompare} className="text-white hover:bg-white/10 h-10">
                <GitCompareArrows className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("compare")}</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="photo-share-button" className="text-white hover:bg-white/10 h-10">
                  <Share2 className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">{t("share")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[110]">
                {typeof navigator !== "undefined" && !!navigator.share && (
                  <DropdownMenuItem data-testid="photo-share-native" onClick={doNativeShare}>
                    <Share2 className="h-4 w-4 mr-2" /> {t("share")}...
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="photo-share-forward" onClick={() => setForwardOpen(true)}>
                  <Forward className="h-4 w-4 mr-2" /> {t("send_to_chat")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="photo-share-whatsapp" onClick={doWhatsApp}>
                  <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="photo-share-copy"
                  onClick={async () => { (await copyLink(shareUrl)) ? toast.success(t("link_copied")) : toast.error("Failed"); }}
                >
                  <Link2 className="h-4 w-4 mr-2" /> {t("copy_link")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="photo-share-download"
                  onClick={async () => { const r = await saveToDevice([{ url: shareUrl, filename: shareName, kind: active?.kind }]); if (r === "saved") toast.success(t("saved_to_gallery_n").replace("{n}", 1)); else if (r === "downloaded") toast.success(t("saved_to_device")); else if (r === false) toast.error(t("something_wrong")); }}
                >
                  <Download className="h-4 w-4 mr-2" /> {t("save_image")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {mode !== "view" && (
          <div className="text-center text-accent text-xs font-medium pb-1 recording-pulse">
            {mode === "reply-area" ? t("reply_area_hint") : t("task_area_hint")}
          </div>
        )}

        {/* Caption of THIS photo (text sent with it) — above the image, never burned into it */}
        {mode === "view" && (message?.content || "").trim() && (
          <div
            data-testid="photo-viewer-caption"
            className="mx-3 mb-1 px-3 py-2 rounded-xl bg-white/10 text-white/95 text-sm leading-snug whitespace-pre-wrap break-words max-h-28 overflow-y-auto"
          >
            {message.content.trim()}
          </div>
        )}

        {/* Image area */}
        <div
          className="relative flex-1 flex items-center justify-center overflow-hidden p-2 min-h-0"
          onWheel={zoomWheel}
          onTouchStart={zoomTouchStart}
          onTouchMove={zoomTouchMove}
          onTouchEnd={zoomTouchEnd}
          onMouseDown={zoomMouseDown}
          onMouseMove={zoomMouseMove}
          onMouseUp={zoomMouseUp}
          onMouseLeave={zoomMouseUp}
          onDoubleClick={zoomDblClick}
          style={mode === "view" ? { touchAction: "none", cursor: zoom > 1 ? "grab" : "zoom-in" } : undefined}
          data-testid="photo-zoom-area"
        >
          {/* Gallery navigation: browse conversation photos without closing the viewer */}
          {mode === "view" && onPrev && (
            <button
              data-testid="photo-nav-prev"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {mode === "view" && onNext && (
            <button
              data-testid="photo-nav-next"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors"
              aria-label="Next photo"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
          <div
            ref={imgWrapRef}
            className={`relative inline-block max-w-full max-h-full ${mode !== "view" ? "region-select" : ""}`}
            style={mode === "view" ? {
              transform: `translate(${pan.x}px, ${pan.y + dragY}px) scale(${zoom * (dragY > 0 ? Math.max(0.88, 1 - dragY / 900) : 1)})`,
              transformOrigin: "center center",
              transition: (gestureRef.current || swipeRef.current) ? "none" : "transform 150ms ease-out",
            } : undefined}
            onMouseDown={areaDown}
            onMouseMove={areaMove}
            onMouseUp={areaUp}
            onTouchStart={areaDown}
            onTouchMove={areaMove}
            onTouchEnd={areaUp}
          >
            <img
              src={mediaSrc(active)}
              alt=""
              draggable={false}
              className="max-w-full max-h-[calc(100vh-180px)] object-contain no-select rounded"
            />
            <TextOverlays media={active} lang={lang} size="lg" />
            {region && mode !== "view" && (
              <div
                className="absolute border-2 border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15 pointer-events-none rounded-sm"
                style={{ left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.w * 100}%`, height: `${region.h * 100}%` }}
              />
            )}
            {busy && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded">
                <span className="text-white text-sm">{t("uploading")}</span>
              </div>
            )}
          </div>

          {/* Floating zoom controls */}
          {mode === "view" && (
            <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1 bg-black/60 rounded-full px-1.5 py-1 backdrop-blur-sm" data-testid="photo-zoom-controls">
              <button
                data-testid="photo-zoom-out"
                onClick={(e) => { e.stopPropagation(); const nz = clampZ(zoom - 0.5); setZoom(nz); if (nz <= 1) setPan({ x: 0, y: 0 }); }}
                className="h-8 w-8 rounded-full text-white hover:bg-white/15 flex items-center justify-center"
                aria-label="Zoom out"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <span className="text-white/85 text-[11px] tabular-nums w-10 text-center" data-testid="photo-zoom-level">{Math.round(zoom * 100)}%</span>
              <button
                data-testid="photo-zoom-in"
                onClick={(e) => { e.stopPropagation(); setZoom(clampZ(zoom + 0.5)); }}
                className="h-8 w-8 rounded-full text-white hover:bg-white/15 flex items-center justify-center"
                aria-label="Zoom in"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              {zoom !== 1 && (
                <button
                  data-testid="photo-zoom-reset"
                  onClick={(e) => { e.stopPropagation(); resetZoom(); }}
                  className="h-8 w-8 rounded-full text-white hover:bg-white/15 flex items-center justify-center"
                  aria-label="Reset zoom"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Category re-classification + Version strip */}
        <div className="px-3 py-2.5 bg-black/60 space-y-2" data-testid="photo-version-strip">
          {active?.job_id && (
            <div className="flex gap-1.5 overflow-x-auto items-center" data-testid="photo-category-row">
              <span className="text-[10px] text-white/50 uppercase tracking-wide shrink-0">{t("change_category")}:</span>
              {["before", "during", "issue", "correction", "after"].map((cat) => (
                <button
                  key={cat}
                  data-testid={`viewer-category-${cat}`}
                  onClick={async () => {
                    try {
                      await api.patch(`/media/${active.id}`, { category: cat });
                      setActive((a) => ({ ...a, category: cat }));
                      setVersions((vs) => vs.map((v) => (v.id === active.id ? { ...v, category: cat } : v)));
                      toast.success(t(cat === "issue" ? "issue_cat" : cat));
                    } catch (e) {
                      toast.error("Failed");
                    }
                  }}
                  className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] border font-medium transition-colors ${active.category === cat ? "bg-primary text-white border-primary" : "text-white/70 border-white/25 hover:bg-white/10"}`}
                >
                  {t(cat === "issue" ? "issue_cat" : cat)}
                </button>
              ))}
            </div>
          )}
          {versions.length > 1 && (
            <div className="flex gap-2 overflow-x-auto items-center">
              {versions.map((v) => (
                <button
                  key={v.id}
                  data-testid={`photo-version-chip-${v.version}`}
                  onClick={() => setActive(v)}
                  className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${active.id === v.id ? "border-[hsl(var(--accent))]" : "border-white/20"}`}
                >
                  <img src={mediaSrc(v)} alt="" className="h-14 w-14 object-cover" />
                  <div className="text-[9px] text-white/80 bg-black/60 px-1 py-0.5 truncate max-w-[56px]">
                    {v.version === 1 ? t("original") : v.version_label || `v${v.version}`}
                  </div>
                </button>
              ))}
            </div>
          )}
          {/* Gallery strip: every photo of the conversation/timeline, click to jump */}
          {gallery && gallery.length > 1 && (
            <div className="flex gap-2 overflow-x-auto items-center" data-testid="photo-gallery-strip">
              {gallery.map((g) => (
                <button
                  key={g.media.id}
                  data-testid={`gallery-thumb-${g.media.id}`}
                  onClick={() => onSelectItem && onSelectItem(g)}
                  className={`shrink-0 rounded-lg overflow-hidden border-2 transition-all ${media.id === g.media.id ? "border-[hsl(var(--accent))]" : "border-white/15 opacity-60 hover:opacity-100"}`}
                >
                  <img src={mediaSrc(g.media)} alt="" className="h-14 w-14 object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <MarkupEditor open={markupOpen} imageSrc={mediaSrc(active)} initialView={{ z: zoom, x: pan.x, y: pan.y }} onSave={handleMarkupSave} onClose={() => setMarkupOpen(false)} />

      {taskDialog && (
        <TaskCreateDialog
          open={!!taskDialog}
          cropDataUrl={taskDialog.cropDataUrl}
          region={taskDialog.region}
          photoMedia={active}
          jobId={jobId}
          conversation={conversation}
          participants={participants}
          onClose={() => setTaskDialog(null)}
          onCreated={() => { setTaskDialog(null); toast.success(t("create_task") + " ✓"); }}
        />
      )}

      {compare && <CompareView open={!!compare} left={compare.left} right={compare.right} onClose={() => setCompare(null)} />}
      <ForwardDialog open={forwardOpen} media={active ? [active] : []} onClose={() => setForwardOpen(false)} />
    </>
  );
}

function TaskCreateDialog({ open, cropDataUrl, region, photoMedia, jobId, conversation, participants, onClose, onCreated }) {
  const { user, t } = useApp();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [jobInstallerId, setJobInstallerId] = useState("");

  // Default "Assign to" = the installer linked to this job (crew leader first)
  useEffect(() => {
    if (!open || !jobId) return;
    let alive = true;
    api.get(`/jobs/${jobId}`).then((res) => {
      if (!alive) return;
      const j = res.data;
      const candidates = [j.crew?.leader_id, ...(j.installer_ids || [])].filter(Boolean);
      const def = candidates.find((id) => (participants || []).some((p) => p.id === id)) || candidates[0] || "";
      setJobInstallerId(def);
      setAssignee((prev) => prev || def);
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const submit = async () => {
    if (!title.trim() || !assignee) return;
    setSaving(true);
    try {
      const cropMedia = await uploadMedia({ base64: dataUrlToBase64(cropDataUrl), jobId, uploadedBy: user.id });
      await api.post("/tasks", {
        job_id: jobId,
        title: title.trim(),
        description: description.trim(),
        assigned_to: assignee,
        created_by: user.id,
        due_label: due.trim(),
        photo_media_id: photoMedia.id,
        region,
        crop_media_id: cropMedia.id,
        conversation_id: conversation?.id || null,
      });
      onCreated();
    } catch (e) {
      toast.error("Task creation failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[120] max-w-md" data-testid="task-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{t("create_task")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <img src={cropDataUrl} alt="" className="h-28 rounded-lg border object-cover" />
          {/* Voice-first: speak the task -> AI fills title/description/assignee/due */}
          <VoiceDraftButton
            kind="task"
            label={voiceReady ? t("speak_again") : t("speak_task")}
            participants={participants}
            onDraft={(d) => {
              if (d.title) setTitle(d.title);
              if (d.description) setDescription(d.description);
              // Spoken name wins; otherwise default to the job's linked installer
              if (d.assignee_id) setAssignee(d.assignee_id);
              else setAssignee((prev) => prev || jobInstallerId);
              if (d.due_label) setDue(d.due_label);
              setVoiceReady(true);
            }}
          />
          {voiceReady && (
            <div data-testid="photo-task-voice-ready-panel" className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <Sparkles className="h-4 w-4" /> {t("draft_ready")}
              </div>
              <div className="text-sm font-semibold">{title}</div>
              <Button
                type="button"
                size="sm"
                data-testid="photo-task-voice-create-now"
                onClick={submit}
                disabled={saving || !title.trim() || !assignee}
                className="w-full"
              >
                <CheckSquare className="h-4 w-4 mr-1.5" /> {saving ? t("sending") : t("task_create_now")}
              </Button>
              {!assignee && <div className="text-[11px] text-muted-foreground">{t("pick_assignee_hint")}</div>}
            </div>
          )}
          <div>
            <Label className="text-xs">{t("title")}</Label>
            <Input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add insulation here" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">{t("description")}</Label>
            <Textarea data-testid="task-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("assign_to")}</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="task-assignee-select" className="mt-1">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="z-[130]">
                  {participants.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`task-assignee-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("due")}</Label>
              <Input data-testid="task-due-input" value={due} onChange={(e) => setDue(e.target.value)} placeholder={t("due_placeholder")} className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button data-testid="task-create-submit" onClick={submit} disabled={saving || !title.trim() || !assignee}>
            {saving ? t("sending") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
